import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ChevronRight } from "lucide-react";
import { createBill } from "../../api/accounting";
import { useApiError } from "../../hooks/useApiError";
import { NeuButton } from "../../components/neu/Neu";
import { BillOcrDropzone } from "./BillOcrDropzone";
import { BillFormFields } from "./BillFormFields";

const CARD_3D = { boxShadow: "0 14px 28px rgba(0,0,0,0.22), 0 8px 12px rgba(0,0,0,0.14), inset 0 1.5px 0 rgba(255,255,255,0.22)" };

// نموذج إضافة فاتورة — رفع + تحليل Gemini + حقول تُملَأ تلقائياً. المحتوى يحل محل قائمة الفواتير.
export const BillFormV2 = ({ accounts, boxes, suppliers, branches, onCancel, onSaved }) => {
  const { t } = useTranslation();
  const describe = useApiError();
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    date: today, due_date: "", amount: "", tax: "0", method: "credit",
    expense_account: "", supplier_id: "", supplier_new: "", branch_id: branches[0]?.id || "",
    supplier_invoice_no: "", description: "", is_recurring: false,
  });
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const categories = useMemo(() => accounts.filter((a) => (a.code || "").startsWith("6")), [accounts]);

  const applyOcr = (r) => setForm((p) => ({
    ...p,
    supplier_invoice_no: r.invoice_no || p.supplier_invoice_no,
    amount:              r.amount != null ? String(r.amount) : p.amount,
    tax:                 r.tax != null ? String(r.tax) : p.tax,
    supplier_new:        !p.supplier_id && r.supplier_name ? r.supplier_name : p.supplier_new,
    date:                r.date || p.date,
    description:         r.description || p.description,
  }));

  const save = async () => {
    setBusy(true);
    try {
      await createBill({
        ...form, amount: Number(form.amount), tax: Number(form.tax || 0),
        cashbox_id: form.method === "credit" ? null : boxes[0]?.id || null,
        supplier_id: form.supplier_id || null,
        supplier_invoice_no: form.supplier_invoice_no || null,
        attachment_url: receipt?.attachment_url || null,
        receipt_hash:   receipt?.receipt_hash || null,
      });
      toast.success(t("acct.billCreated"));
      onSaved();
    } catch (error) { toast.error(describe(error)); }
    finally { setBusy(false); }
  };

  return (
    <div className="animate-rise-in rounded-3xl border border-primary/20 bg-background/70 p-6" style={CARD_3D} data-testid="bill-form-v2">
      <div className="mb-5 flex items-center justify-between">
        <button type="button" onClick={onCancel} data-testid="bill-form-back"
                className="flex items-center gap-2 rounded-2xl border border-border/60 px-3 py-1.5 text-xs font-bold hover:-translate-y-0.5">
          <ChevronRight className="h-4 w-4 rtl:rotate-180" /> {t("app.back")}
        </button>
        <h2 className="text-xl font-black text-primary">{t("acct.bills.new.title")}</h2>
      </div>

      <BillOcrDropzone onExtracted={applyOcr} onReceipt={setReceipt} receipt={receipt} />
      <BillFormFields form={form} set={set} categories={categories} suppliers={suppliers} branches={branches} />

      <div className="mt-6 flex justify-end gap-3">
        <NeuButton data-testid="bill-cancel" onClick={onCancel}>{t("app.cancel")}</NeuButton>
        <NeuButton variant="primary" data-testid="bill-save"
                   disabled={busy || !form.amount || !form.branch_id || !form.expense_account}
                   onClick={save}>{t("acct.bills.new.save")}</NeuButton>
      </div>
    </div>
  );
};
